<template>
	<wrapper :results="true">
		<template slot="wrapper">
			<div class="o-shell">
				<app-header />

				<main class="o-main">
					<div class="c-article">
						<div
							class="c-article__content c-article__content--results"
						>
							<h1>
								Retirement Calculator -
								<span class="c-highlight">Output</span>
							</h1>

							<p>
								Now that you have a guide for determining
								roughly how much you’d like to spend in each
								category, you’re ready to add everything up. To
								get your percentage, you’ll need to add your
								salary in the field below. If you want to take
								the next step in this process and translate your
								annual amount to the total amount you will need
								over the course of your full retirement (to know
								the total amount you need to accumulate from
								until then, for example), simply multiply the
								annual amount by the number of years you expect
								to be in retirement.
							</p>
						</div>

						<div class="c-article__content-calc">
							<div class="c-article__content-calc--text">
								<label class="label">Choose salary type:</label>

								<select v-model="salaryType" class="select">
									<option value="monthly"
										>Monthly Salary</option
									>
									<option value="annual"
										>Annual Salary</option
									>
								</select>
							</div>
							<div class="c-article__content-calc--text">
								<label class="label"
									>Input your Current Salary</label
								>

								<input
									type="number"
									v-model="currentSalary"
									@focus="currentSalary = ''"
									@blur="
										currentSalary =
											currentSalary === ''
												? 0
												: currentSalary
									"
									min="0"
								/>
							</div>
							<!-- /.c-article__content-calc--text -->
							<div class="c-article__content-calc--text">
								<label class="label"
									>Input number of years of expected
									retirement:</label
								>

								<input
									type="number"
									v-model="currentRetirementPeriod"
									@focus="currentRetirementPeriod = ''"
									@blur="
										currentRetirementPeriod =
											currentRetirementPeriod === ''
												? 0
												: currentRetirementPeriod
									"
									min="0"
								/>
							</div>
							<!-- /.c-article__content-calc--text -->
						</div>
						<!-- /.c-article__content-calculations -->
						<div class="c-article__img c-article__img--results">
							<img src="~assets/Artwork-last@2x.png" />
						</div>
						<!-- /.c-article__img -->
					</div>

					<div class="c-trigger-calc">
						<button
							type="button"
							@click="setValues"
							class="c-button"
						>
							{{
								(!isEstimated ? 'C' : 'Rec') +
									'alculate Your Estimated Annual Spend'
							}}
						</button>
					</div>
					<!-- /.c-trigger-calc -->
					<div class="c-results" v-if="isEstimated">
						<div class="c-results__header">
							<h1 v-if="currentSalary >= total">
								Great! Your estimated annual spend is lower than
								your salary!
							</h1>

							<h1 v-if="currentSalary < total">
								Your estimated annual spend is greater than your
								salary!
							</h1>
						</div>
						<!-- /.c-results__header -->
						<div class="c-results__body">
							<div class="c-results__container">
								<div
									class="c-article__content-calc c-article__content-calc--container"
								>
									<div
										class="c-article__content-calc--text c-article__content-calc--container"
									>
										<label>Total savings needed:</label>

										<span class="c-highlight"
											>$ {{ formattedTotal }}</span
										>
									</div>
									<!-- /.c-article__content-calc--text -->
									<div
										class="c-article__content-calc--text c-article__content-calc--container"
									>
										<label
											>Total annual income needed in
											retirement</label
										>

										<span class="c-highlight"
											>$ {{ totalAnnualIncome }}</span
										>
									</div>
									<!-- /.c-article__content-calc--text -->
									<div
										class="c-article__content-calc--text c-article__content-calc--container"
									>
										<label
											>Percent of salary needed in
											retirement</label
										>

										<span class="c-highlight"
											>{{ salaryPercentNeeded }}%</span
										>
									</div>
									<!-- /.c-article__content-calc--text -->
								</div>
								<!-- /.c-article__content-calculations -->
							</div>
							<!-- /.c-results__container -->
							<div
								class="c-results__container c-results__container--flex"
							>
								<div class="c-results__chart">
									<div class="c-results__chart-header">
										<h4>
											Current Salary vs. Salary Needed in
											Retirement
										</h4>
									</div>
									<!-- /.c-chart__header -->
									<div class="c-results__chart-body">
										<bar-chart :data="barChartData" />
									</div>
									<!-- /.c-chart__body -->
								</div>
								<!-- /.c-results__chart -->
								<div class="c-results__chart">
									<div class="c-results__chart-header">
										<h4>Retirement Spend</h4>
									</div>
									<!-- /.c-chart__header -->
									<div class="c-results__chart-body">
										<pie-chart :data="pieChartData" />
									</div>
									<!-- /.c-chart__body -->
								</div>
								<!-- /.c-results__chart -->
							</div>
							<!-- /.c-results__container -->
							<div class="c-trigger-calc">
								<a class="c-button" href="/">Start Over</a>

								<button
									@click="downloadResults"
									class="c-button c-button--download"
									type="button"
								>
									Download Results
								</button>
							</div>
							<!-- /.c-trigger-calc -->
						</div>
						<!-- /.c-results__body -->
					</div>
					<!-- /.c-results -->
				</main>

				<footer class="c-footer">
					<div class="o-shell">
						<div class="c-copyright">
							<p>
								&copy; 2018 Retirement Calculator. All rights
								reserved.
							</p>
						</div>
						<!-- /.c-copyright -->
					</div>
					<!-- /.o-shell -->
				</footer>
				<!-- /.c-footer -->
			</div>
		</template>
	</wrapper>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import jsFileDownload from 'js-file-download';
import axios from 'axios';

import Header from 'components/header/header.vue';
import Wrapper from 'components/wrapper/Wrapper.vue';
import Button from 'components/button/button.vue';
import BarChart from 'components/charts/bar-chart.vue';
import PieChart from 'components/charts/pie-chart.vue';

import { formatCurrency } from 'utils';

export default {
	data() {
		return {
			currentSalary: 0,
			currentRetirementPeriod: 0,
			salaryType: 'monthly',
		};
	},

	components: {
		'app-header': Header,
		wrapper: Wrapper,
		btn: Button,
		'bar-chart': BarChart,
		'pie-chart': PieChart,
		jsFileDownload,
	},

	computed: {
		...mapGetters([
			'total',
			'isEstimated',
			'salary',
			'retirementPeriod',
			'steps',
			'totalIncomeNeeded',
		]),

		totalAnnualIncome() {
			return formatCurrency(this.total * this.retirementPeriod);
		},

		salaryPercentNeeded() {
			const result = (this.total / this.salary) * 100;
			return isNaN(result) ? 0 : result.toFixed(2);
		},

		formattedTotal() {
			return formatCurrency(this.total);
		},

		barChartData() {
			return {
				datasets: [
					{
						label: 'Current salary',
						data: [this.salary],
						backgroundColor: '#FF6600',
						borderWidth: 0,
					},
					{
						label: 'Total annual income needed in retirement',
						data: [this.totalIncomeNeeded],
						backgroundColor: '#F98155',
						borderWidth: 0,
					},
				],
			};
		},

		pieChartData() {
			const data = {
				datasets: [
					{
						data: [],
						backgroundColor: [],
						borderWidth: 1,
					},
				],
				labels: [],
			};

			this.steps.forEach((step) => {
				data.labels.push(step.title);
				data.datasets[0].data.push(step.total);
				data.datasets[0].backgroundColor.push(step.color);
			});

			return data;
		},
	},

	methods: {
		...mapActions(['setCalculated', 'setSalary', 'setRetirementPeriod']),

		setValues() {
			const salary =
				this.salaryType == 'monthly'
					? this.currentSalary * 12
					: this.currentSalary;
			this.setSalary(salary);
			this.setRetirementPeriod(this.currentRetirementPeriod);
			this.setCalculated();
		},

		downloadResults() {
			// empty csv data
			const csvdata = [];

			// populate the csv steps data
			this.steps.forEach((step) => {
				const period = {
					1: 'per year',
					12: 'per month',
					52: 'per week',
				};

				csvdata.push([step.title]);
				csvdata.push([
					'Input Label',
					'Purchase/Spend Frequency',
					'Period',
					'Amount($) per Occasion',
					'Total',
				]);

				step.rows
					.sort((rowA, rowB) => rowA.rowIndex - rowB.rowIndex)
					.forEach((row) => {
						csvdata.push([
							row.title || 'N/A',
							row.spendFrequency || 0,
							period[row.period] || '',
							row.amount || 0,
							row.total || 0,
						]);
					});

				csvdata.push([' ']);
			});

			// add the summary to the csv
			csvdata.push(['Summary']);
			csvdata.push(['Total savings needed', this.formattedTotal]);
			csvdata.push([
				'Total annual income needed in retirement',
				this.totalAnnualIncome,
			]);
			csvdata.push([
				'Percent of salary needed in retirement',
				this.salaryPercentNeeded,
			]);

			// format the data and build the csv content
			let csvContent = '';
			csvdata.forEach((row) => {
				row.forEach((column) => {
					if (typeof column === 'undefined') {
						csvContent += `"N/A";`;
						return;
					}

					column = column.toString().replace('"', '\\"');
					csvContent += `"${column}";`;
				});
				csvContent += '\n';
			});

			// generate the csv link and trigger the download
			let hiddenElement = document.createElement('a');
			hiddenElement.href =
				'data:text/csv;charset=utf-8,' + encodeURI(csvContent);
			hiddenElement.target = '_blank';
			hiddenElement.download = 'results.csv';
			hiddenElement.click();
		},
	},
};
</script>

<style lang="scss" scoped>
.c-highlight {
	line-height: 1.2;
	background-color: #f98155;
	display: inline-block;
	color: #fff !important;
	padding: 10px 5px;
}

.c-article {
	padding-bottom: 4rem;

	&__content {
		&--results {
			margin-bottom: 0;

			h1 {
				font-weight: 400;
				color: #333;
				margin: 0;
				span {
					color: #d8d8d8;
				}

				@media (max-width: 767px) {
					font-size: 1.7rem;
					text-align: center;
					padding-bottom: 2.5rem;
				}
			}

			p {
				font-weight: 400;
				margin: 0;
				padding-top: 1.5625rem;
				padding-bottom: 3.125rem;

				@media (max-width: 767px) {
					font-size: 1rem;
				}
			}
			&-container {
				width: 100%;
				display: block;
			}

			@media (max-width: 767px) {
				width: 100%;
			}
		}

		&-calc {
			width: 50%;
			font-family: 'Roboto', sans-serif;
			font-weight: 500;

			@media (max-width: 1200px) {
				width: 100%;
				display: flex;
				justify-content: space-between;
			}

			@media (max-width: 767px) {
				display: flex;
				flex-direction: column;
			}

			&--text {
				padding-bottom: 1.875rem;
				font-size: 1.125rem;

				@media (max-width: 1200px) {
					display: flex;
					flex-direction: column;
				}

				label {
					width: 35%;
					display: inline-block;
					margin-right: 3.4375rem;
					color: #333333;
					vertical-align: bottom;

					@media (max-width: 1200px) {
						width: 90%;
						text-align: center;
						margin: 0 auto;
					}

					@media (max-width: 767px) {
						padding-bottom: 1.3125rem;
					}
				}

				span {
					text-align: center;
					vertical-align: bottom;
					min-width: 6.25rem;

					@media (max-width: 1200px) {
						width: 50%;
						margin: auto;
					}

					@media (max-width: 1200px) {
						width: 25%;
						margin: auto;
					}
				}

				input {
					font-size: 18px;
					padding: 0.5rem 0 0.5rem 0.625rem;
					border: none;
					border-bottom: 1px solid #d8d8d8;
					position: relative;
					text-align: center;
					width: 17%;
					background-color: rgba(127, 210, 232, -88);

					&:focus {
						outline: none;
					}

					@media (max-width: 1200px) {
						width: 70%;
						margin: 0 auto;
						line-height: 1.4;
					}
				}

				.select {
					border: none;
					color: #333;
					background-color: inherit;

					@media (max-width: 756px) {
						margin: auto;
					}
				}
			}

			&--container {
				width: 100%;
				display: flex;
				align-items: flex-end;
				justify-content: center;
				padding-bottom: 1.5625rem;

				span {
					display: flex;
					justify-content: center;
					align-items: center;
					vertical-align: center;
				}

				label {
					vertical-align: center;
					display: flex;
					justify-content: center;
					align-items: center;
					width: 44%;
				}

				@media (max-width: 756px) {
					label {
						width: 100%;
					}
				}
			}
		}
	}

	&__img {
		width: 55%;
		left: 40.625rem;
		bottom: 1.25rem;
		z-index: -1;

		@media (max-width: 1200px) {
			width: 25rem;
			top: 8.125rem;
			left: 33.125rem;
		}

		@media (max-width: 1024px) {
			width: 18.75rem;
			bottom: 10.625rem;
			left: 30rem;
		}

		@media (max-width: 767px) {
			display: none;
		}

		img {
			width: 100%;
		}
	}

	@media (max-width: 1024px) {
		padding-bottom: 0;
	}
}

.c-results {
	&__header {
		text-align: center;
		padding: 2.6875rem 0 3.875rem 0;

		h1 {
			margin: 0;
			font-weight: 500;

			@media (max-width: 767px) {
				font-size: 1.4rem;
			}
		}
	}

	&__container {
		display: block;

		@media (max-width: 1200px) {
			display: flex;
			flex-wrap: wrap;
		}

		&--flex {
			display: flex;
			justify-content: space-around;
		}

		span {
			@media (max-width: 1200px) {
				width: 45%;
			}
		}

		input {
			@media (max-width: 1200px) {
				width: 50%;
			}
		}
	}

	&__chart {
		width: 100%;

		&-header {
			h4 {
				width: 40%;
				margin: 0 auto;
				padding-bottom: 3.125rem;
				padding-right: 1.25rem;
				text-align: center;
				min-height: 6.25rem;

				@media (max-width: 767px) {
					width: 80%;
				}
			}
		}

		&-body {
			width: 80%;
			margin: 0 auto;

			@media (max-width: 1200px) {
				margin: auto;
			}
		}

		@media (max-width: 1200px) {
			width: 100%;
			margin: auto;
		}
	}
}

.c-footer {
	.o-shell {
		padding: 1.875rem 2%;

		@media (max-width: 1200px) {
			padding-bottom: 0;
		}
	}

	@media (max-width: 1200px) {
		padding-top: 2rem;

		.o-shell {
			padding-top: 0;
		}
	}

	@media (max-width: 756px) {
		padding-top: 0;
	}
}

.c-copyright {
	p {
		font-size: 0.75rem;

		@media (max-width: 1200px) {
			text-align: center;
		}
	}
}

.c-trigger-calc {
	padding: 4rem 0;
	text-align: center;

	@media (max-width: 1200px) {
		padding: 2rem 0;
	}

	@media (max-width: 756px) {
		a {
			margin-bottom: 1rem;
			width: 90%;
		}

		button {
			width: 90%;
			font-size: 1.1rem;
		}
	}
}
</style>
